import React, { useEffect, useState } from "react";
import { View, StyleSheet, Platform } from "react-native";

import { Text, Cover } from "components";
import Title from "./Title";
import Actions from "./Actions";
import Counter from "./Counter";
import StepChanger from "../../StepChanger";

import { priceFormatter } from "utils";
import { useRecoilValue } from "recoil";
import { factorStepsAtom } from "stores";
import { sizes, colors, strings } from "assets/constants";
import { useChangeStepFlags } from "../../hooks/useChangeStepFlags";
import tinycolor from "tinycolor2";
import { useChmod } from "hooks";

type RemoveEventT = (itemId: number) => void;
type ChangeCountEventT = (id: number, price: number, count: number) => void;
type CloseChangeStepEventT = (itemId: number) => void;
type ChangeStepEventT = (id: number, step: string, count: number) => void;

type FactorItemCardProps = {
  item: FactorItemT;
  onRemove: RemoveEventT;
  onChangeCount: ChangeCountEventT;
  onChangeStepData: ChangeStepEventT;
  onCloseChangeStep: CloseChangeStepEventT;
  disableAction?: boolean;
};

const activeBorderColor = tinycolor(colors.theme).lighten(45).toString();

const FactorItemCard: React.FC<FactorItemCardProps> = (props) => {
  const { chmod } = useChmod();
  const steps = useRecoilValue(factorStepsAtom);
  const {
    item,
    onRemove,
    onChangeCount,
    disableAction,
    onChangeStepData,
    onCloseChangeStep,
  } = props;

  const {
    goToCountEditingMode,
    multiItemsChangeStepMode,
    singleItemChangeStepMode,
    goToSingleItemChangeStepMode,
    closeSingleItemChangeStepMode,
    singleItemChangeStepModeForAll,
  } = useChangeStepFlags();

  const [countState, setCountState] = useState(+item?.count);
  const [targetStep, setTargetStep] = useState(steps[item?.status?.priority]);
  const [changeStepCount, setChangeStepCount] = useState<number>();
  const [counterStep, setCounterStep] = useState(1);

  useEffect(() => {
    setChangeStepCount(0);
  }, [multiItemsChangeStepMode]);

  useEffect(() => {
    setCountState(+item?.count);
    setTargetStep(steps[item?.status?.priority]);
  }, [item]);

  const _onRemove = () => onRemove(item.id);

  const _onChangeItemCount = (count: number) => {
    goToCountEditingMode();

    setCountState(count);
    onChangeCount(item?.id, +item?.price, count);
  };

  const _goToSingleChangeStepMode = () => {
    goToSingleItemChangeStepMode(item.id);
    setChangeStepCount(1);
    onChangeStepData(item?.id, targetStep.id, 1);
  };

  const _onChangeItemStep = (step: FactorStatusT) => {
    setTargetStep(step);
    onChangeStepData(item?.id, step.id, changeStepCount);
  };

  const _onChangeItemStepCount = (count: number) => {
    setChangeStepCount(count);
    onChangeStepData(item.id, targetStep.id, count);
  };

  const _onCloseChangeStep = () => {
    closeSingleItemChangeStepMode(item.id);
    onCloseChangeStep(item.id);
  };

  const _renderFooter = () => {
    if (multiItemsChangeStepMode) {
      return (
        <>
          <View style={styles.row}>
            <View style={styles.count}>
              <Text size={"xl"} weight="extraBold">
                {item?.count}
              </Text>
            </View>

            <Text size={"xl"} weight="bold">
              {" "}
              از{" "}
            </Text>

            <Counter
              disableAction={disableAction}
              defaultValue={0}
              maxCount={+item?.count}
              minCount={0}
              onChange={_onChangeItemStepCount}
            />
          </View>
        </>
      );
    }

    if (singleItemChangeStepMode(item.id)) {
      return (
        <>
          <View style={{ left: -6 }}>
            <StepChanger
              onChange={_onChangeItemStep}
              defaultStepPriority={item?.status?.priority}
            />
          </View>

          <Cover />

          {item?.item_description ? null : (
            <View style={styles.row}>
              <Text
                top={Platform.OS === "web" ? -1 : 0}
                size={"xl"}
                weight="extraBold"
                color={colors.text.dark}
              >
                {item?.count}
              </Text>

              <Text top={Platform.OS === "web" ? -1 : 0} size={"xl"}>
                {" "}
                از{" "}
              </Text>

              <Counter
                defaultValue={1}
                disableAction={disableAction}
                onChange={_onChangeItemStepCount}
                maxCount={+item?.count}
              />
            </View>
          )}
        </>
      );
    }

    if (singleItemChangeStepModeForAll) {
      return <></>;
    }

    if (item?.item_description) {
      return (
        <View style={{ flex: 1 }}>
          <Text>{item?.item_description}</Text>
        </View>
      );
    }

    return (
      <>
        {item?.product?.is_binary === 1 && (
          <View
            style={{
              position: "absolute",
              bottom: -10,
              left: 0,
              borderWidth: 1,
              paddingHorizontal: 12,
              borderRadius: 10,
              backgroundColor: colors.white,
              borderColor: colors.lightBorder,
            }}
          >
            <Text top={-1} weight="extraBold" color={colors.darkWarning}>
              جفتی
            </Text>
          </View>
        )}
        <View style={[styles.row, styles.price]}>
          <View style={{ marginRight: sizes.space / 2 }}>
            <Text weight="bold" size={"xs"}>
              {strings.currency}
            </Text>
          </View>
          <Text size={"lg"} weight="extraBold" style={{ letterSpacing: 1.05 }}>
            {priceFormatter(+item?.price * +countState)}
          </Text>
        </View>
        <Cover />
        <View
          style={{
            borderWidth: 1,
            flexDirection: "row",
            alignItems: "center",
            borderColor: colors.lightBorder,
            padding: 5,
            borderRadius: 10,
          }}
        >
          <Counter
            counterStep={item?.product?.is_binary === 1 ? 2 : 1}
            disableAction={chmod("factor", "PUT") ? false : disableAction}
            defaultValue={+item?.count}
            onChange={_onChangeItemCount}
          />
          <Text color={colors.text.muted} weight="extraBold" size={"sm"}>
            {" "}
            ویرایش تعداد{" "}
          </Text>
        </View>
      </>
    );
  };

  const active = multiItemsChangeStepMode && changeStepCount > 0 ? true : false;

  return (
    <View
      style={{
        ...styles.container,
        borderWidth: 2,
        borderColor: active ? activeBorderColor : colors.white,
      }}
    >
      {active && <View style={styles.dot} />}

      {/* //* header */}
      <View style={[styles.row, styles.header]}>
        {chmod("factor", "PUT") && (
          <Actions
            item={props.item}
            onRemove={_onRemove}
            onCloseChangeStep={_onCloseChangeStep}
            goToChangeStep={_goToSingleChangeStepMode}
            disableAction={disableAction}
          />
        )}
        <Cover />
        <Title item={props.item} />
      </View>

      {/* //* footer */}

      <View style={[styles.row, styles.footer]}>{_renderFooter()}</View>
    </View>
  );
};

export default FactorItemCard;

const styles = StyleSheet.create({
  container: {
    width: "100%",
    borderRadius: sizes.radius.xl,
    borderTopRightRadius: 10,
    paddingHorizontal: sizes.space / 2,
    backgroundColor: colors.white,
  },

  row: { flexDirection: "row", alignItems: "center" },

  header: { height: 50 },
  footer: { height: 55, paddingBottom: 5 },

  price: { height: 37 },

  count: { minWidth: 35, alignItems: "center", justifyContent: "center" },

  dot: {
    top: 10,
    left: 10,
    width: 10,
    height: 10,
    borderRadius: 10,
    position: "absolute",
    backgroundColor: colors.theme,
  },
});
